import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { AttributeDefinitionRequest, ProductAttributeDefinitionsService } from '@src/app/shared/backend/clients';
import { SessionService } from '@src/app/shared/services/session.service';
import { ProductAttributeDataTypeService } from '@src/app/shared/services/productAttributeDataType.service';
import { AttributeDataType } from '@src/app/shared/enums/AttributeDataType.enum';

interface LanguageItem {
  id?: number;
  language: string;
  code: string;
}

@Component({
    selector: 'app-add-product-attribute-definitions',
    templateUrl: './add-product-attribute-definitions.component.html',
    styleUrl: './add-product-attribute-definitions.component.scss',
    host: {
        class: 'app-add-product-attribute-definitions'
    },
    standalone: false
})

export class AddProductAttributeDefinitionsComponent extends BaseViewComponent implements OnInit {
  languages: LanguageItem[] = [];

  dataTypeOptions = this.productAttributeDataTypeService.getProductAttributeDataTypeOptions();

  showAttributeTranslations = false;

  productAttributeDefinitionForm: FormGroup = this.fb.group(
    {
      attributeCode: ['', [Validators.required, Validators.maxLength(100)]],
      dataType: [[AttributeDataType.FIXED_VALUES], Validators.required],
      allowMultipleValues: [false, Validators.required],
      changesOverTime: [false, Validators.required],
      attributeTranslations: this.fb.group({}),
      valueCodes: this.fb.array([])
    },
    {
      validators: [this.requireValueCodeForFixedValuesType()]
    }
  );

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private translate: TranslateService,
    private session: SessionService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private productAttributeDataTypeService: ProductAttributeDataTypeService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    await this.loadLanguages();
    this.buildAttributeTranslationsGroup();
    this.handleDataTypeChanges();

    if (this.isFixedValuesType && this.valueCodes.length === 0) {
      this.addValueCode();
    }
  }

  get valueCodes(): FormArray {
    return this.productAttributeDefinitionForm.get('valueCodes') as FormArray;
  }

  get attributeTranslationsGroup(): FormGroup {
    return this.productAttributeDefinitionForm.get('attributeTranslations') as FormGroup;
  }

  get isFixedValuesType(): boolean {
    return this.productAttributeDefinitionForm.get('dataType')?.value?.[0] === AttributeDataType.FIXED_VALUES;
  }

  private async loadLanguages(): Promise<void> {
    const languages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = languages.filter(language => language.isEnabled);

    return new Promise((resolve) => {
      this.translate.get('PORTAL_LANGUAGES').subscribe(() => {
        this.languages = enabledLanguages
          .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
          .map(lang => {
            const code = lang.code || 'unknown';

            return {
              id: lang.id,
              language: `PORTAL_LANGUAGES.${code.toUpperCase()}`,
              code
            };
          });

        resolve();
      });
    });
  }

  private buildAttributeTranslationsGroup(): void {
    const group = this.fb.group({});

    this.languages.forEach(language => {
      group.addControl(language.code, this.fb.control(''));
    });

    this.productAttributeDefinitionForm.setControl('attributeTranslations', group);
  }

  private createTranslationsGroup(): FormGroup {
    const group = this.fb.group({});

    this.languages.forEach(language => {
      group.addControl(language.code, this.fb.control(''));
    });

    return group;
  }

  private createValueCodeGroup(): FormGroup {
    return this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(100)]],
      codeIsActive: [true, Validators.required],
      showTranslations: [false],
      translations: this.createTranslationsGroup()
    });
  }

  private handleDataTypeChanges(): void {
    this.productAttributeDefinitionForm.get('dataType')?.valueChanges.subscribe(() => {
      if (this.isFixedValuesType) {
        if (this.valueCodes.length === 0) {
          this.addValueCode();
        }
      } else {
        this.showAttributeTranslations = false;
        this.clearValueCodes();
      }

      this.productAttributeDefinitionForm.updateValueAndValidity();
    });
  }

  private clearValueCodes(): void {
    while (this.valueCodes.length > 0) {
      this.valueCodes.removeAt(0);
    }
  }

  private requireValueCodeForFixedValuesType(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {

      const dataTypeControlValue = control.get('dataType')?.value;
      const selectedType = Array.isArray(dataTypeControlValue) ? dataTypeControlValue[0] : null;
      const valueCodes = control.get('valueCodes') as FormArray | null;

      if (selectedType !== 'fixed_values') {
        return null;
      }

      if (!valueCodes || valueCodes.length === 0) {
        return { valueCodeRequired: true };
      }

      const hasAtLeastOneValue = valueCodes.controls.some(item => {
        const code = item.get('code')?.value;
        return !!String(code ?? '').trim();
      });

      return hasAtLeastOneValue ? null : { valueCodeRequired: true };
    };
  }

  addValueCode(): void {
    this.valueCodes.push(this.createValueCodeGroup());
    this.productAttributeDefinitionForm.updateValueAndValidity();
  }

  removeValueCode(index: number): void {
    this.valueCodes.removeAt(index);
    this.productAttributeDefinitionForm.updateValueAndValidity();
  }

  toggleAttributeTranslations(): void {
    if (!this.isFixedValuesType) {
      return;
    }

    this.showAttributeTranslations = !this.showAttributeTranslations;
  }

  toggleValueTranslations(index: number): void {
    const group = this.valueCodes.at(index) as FormGroup;
    const currentValue = group.get('showTranslations')?.value ?? false;

    group.get('showTranslations')?.setValue(!currentValue);
  }

  getValueTranslationsGroup(index: number): FormGroup {
    return this.valueCodes.at(index).get('translations') as FormGroup;
  }

  private mapTranslations(group: FormGroup): { languageId?: number; code: string; value: string }[] {
    return this.languages
      .map(language => {
        const value = String(group.get(language.code)?.value ?? '').trim();

        return {
          languageId: language.id,
          code: language.code,
          value
        };
      })
      .filter(item => !!item.value);
  }

  async submitForm(): Promise<void> {
    this.productAttributeDefinitionForm.markAllAsTouched();
    this.productAttributeDefinitionForm.updateValueAndValidity();

    if (this.productAttributeDefinitionForm.invalid) {
      return;
    }
    const payload: AttributeDefinitionRequest = {
      definitionCode: String(this.productAttributeDefinitionForm.get('attributeCode')?.value ?? '').trim(),
      attributeDataType: this.productAttributeDefinitionForm.get('dataType')?.value?.[0] ?? 0,
      allowMultipleValues: !!this.productAttributeDefinitionForm.get('allowMultipleValues')?.value,
      changesOverTime: !!this.productAttributeDefinitionForm.get('changesOverTime')?.value,
      attributeDefinitionTranslations: this.isFixedValuesType
        ? this.languages
          .map(language => ({
            isoCode: language.code,
            translatedName: String(this.attributeTranslationsGroup.get(language.code)?.value ?? '').trim()
          }))
          .filter(item => !!item.translatedName)
        : [],
      attributeValues: this.isFixedValuesType
        ? this.valueCodes.controls.map((control) => {
            const group = control as FormGroup;
            const translationsGroup = group.get('translations') as FormGroup;

            return {
              valueCode: String(group.get('code')?.value ?? '').trim(),
              isActive: group.get('codeIsActive')?.value ?? '',
              attributeValueTranslations: this.languages
                .map(language => ({
                  isoCode: language.code,
                  translatedValue: String(translationsGroup.get(language.code)?.value ?? '').trim()
                }))
                .filter(item => !!item.translatedValue)
            };
          })
          .filter(item => !!item.valueCode)
      : []
    };

    await firstValueFrom(this.productAttributeDefinitionsService.apiProductAttributeDefinitionsCreatePost(payload));

    this.router.navigate(['/product-attribute-definitions']);
  }
}
